import React, { memo, useMemo, useState, useEffect } from 'react';
import {
    NoResults,
    algoliaCache,
    // ProductHits
} from '~/features/productsList/components';
import { AlgoliaClientSearch } from '~/features/productsList/components';
import {
    M140ProductListModule,
    M170SearchPage,
    MarketingSpot,
    P40ProductListPage,
    P41ProductListPage,
} from '~/lib/data-contract';
import { useFrame, useTranslation } from '~/shared/utils';
import { ResultsCount } from '../../../../features/productsList/components/filters/results-count/results-count.component';
import DynamicFilters from '~/features/productsList/components/filters/dynamic-filters/dynamic-filters';

import {
    StyledCategoriesContainer,
    StyledProductList,
    StyledProductListContainer,
    StyledProductsHeader,
} from './styled';
import { Visibility } from '~/shared/utils/jsx';

import { usePage } from '~/templates/pages';
import { N50Breadcrumb, N40SideMenu } from '~/features/navigation';
import { useAlgoliaAnalytics } from '~/shared/hooks';
import { MobileCategories } from './components/MobileCategories';
import { useSideMenu } from '~/shared/utils/sidemenu';
import { Spacer } from '~/theme';
import { Headline } from '../Headline/Headline';
import { useRouter } from 'next/router';
import { useAlgoliaInitialState } from '~/shared/utils/algolia';
import { usePLPGridLayout } from '~/features/productsList/hooks/usePLPGridLayout';
import { useModule } from '~/templates';
import { useAlgoliaConfig } from '$shared/hooks/useAlgoliaConfig';
import { useFilterUIState } from '$features/productsList/hooks';
import { useMount } from 'react-use';
import { ProductsGrid } from '~/features/productsList/components/products-grid/products-grid.component';
import { Container } from '../container/container.component';

export type M140ProductListProps = M140ProductListModule & { marketingContent?: MarketingSpot[] };

export const M140ProductsList = memo((props: M140ProductListProps) => {
    const { itemsPerPage, category, context, sortIndex, marketingContent } = props;
    const { asPath, locale, replace: routerReplace, query: routerQuery } = useRouter();
    const { orderIndexes } = useAlgoliaConfig();
    const { index = 0 } = useModule();
    const { data: frameData } = useFrame();
    // Handle sortIndex before getting initial state to avoid double loading
    const activeSortingKey = useMemo(() => {
        if (sortIndex && !routerQuery.sortBy) {
            return orderIndexes.find((i) => i.key.includes(sortIndex))?.key;
        }
        return null;
    }, [sortIndex, routerQuery.sortBy, orderIndexes]);

    const { setPreSelectedIndexKey } = useFilterUIState();
    const { trackSelectedCategory } = useAlgoliaAnalytics();

    // Build the path with sort parameter if needed and set pre-selected index key immediately
    const pathForInitialState = useMemo(() => {
        let path = asPath.replace(new RegExp(`^/${locale}`), '');

        // If we have an activeSortingKey and no existing sortBy query, set it up
        if (activeSortingKey && !routerQuery.sortBy) {
            // Set the pre-selected index key synchronously
            setPreSelectedIndexKey(activeSortingKey);

            // Add to URL path for initial state
            const hasQuery = path.includes('?');
            path = `${path}${hasQuery ? '&' : '?'}sortBy=${activeSortingKey}`;
        }

        return path;
    }, [asPath, locale, activeSortingKey, routerQuery.sortBy, setPreSelectedIndexKey]);

    const { initialState } = useAlgoliaInitialState(pathForInitialState);

    const { translate } = useTranslation();
    const page = usePage<P40ProductListPage | P41ProductListPage | M170SearchPage>();
    const gridLayout = usePLPGridLayout(page.type);
    const { data: sidemenu } = useSideMenu();

    const breadcrumb = page?.breadcrumb || {};
    const breadcrumbItems = breadcrumb?.items || [];
    const currentPageIndex = (breadcrumbItems?.length || 0) - 1;
    const currentPage = breadcrumbItems?.length ? breadcrumbItems[currentPageIndex] : null;

    const hitsPerPage = itemsPerPage ?? 24;

    // Control when to render Algolia to prevent double loading
    const [shouldRenderAlgolia, setShouldRenderAlgolia] = useState(
        !activeSortingKey || !!routerQuery.sortBy
    );

    // Update the router URL and control Algolia rendering
    useEffect(() => {
        if (activeSortingKey && !routerQuery.sortBy) {
            // Update router first
            routerReplace(
                {
                    pathname: asPath,
                    query: {
                        ...routerQuery,
                        sortBy: activeSortingKey,
                    },
                },
                undefined,
                { shallow: true }
            ).then(() => {
                // Only render Algolia after router update is complete
                setShouldRenderAlgolia(true);
            });
        } else if (!activeSortingKey || routerQuery.sortBy) {
            // No sorting key or already have sortBy in query, render immediately
            setShouldRenderAlgolia(true);
        }
    }, [activeSortingKey, routerQuery.sortBy, routerReplace, asPath, routerQuery]);

    useMount(() => {
        if (category) {
            trackSelectedCategory(`categoryKeys:${category}`);
        }
    });

    return (
        <Container spacingTop={index === 0 ? 'none' : 'default'} hasGutter>
            {shouldRenderAlgolia && (
                <AlgoliaClientSearch
                    hitsPerPage={hitsPerPage}
                    algoliaApp={frameData?.search?.algolia?.appKey || ''}
                    algoliaToken={frameData?.search?.algolia?.apiToken || ''}
                    category={category}
                    context={context}
                    initialState={initialState}
                >
                    <StyledProductsHeader topMargin={index === 0 ? 'default' : 'little'}>
                        <N50Breadcrumb>
                            <Visibility.HiddenFrom md>
                                <ResultsCount spacing="header" />
                            </Visibility.HiddenFrom>
                        </N50Breadcrumb>
                        <Spacer space="2" />
                        <Visibility.VisibleFrom md>
                            <Headline variant="display1">
                                {routerQuery?.pageTitle || currentPage?.title || translate('commerce.allProducts')}
                            </Headline>
                        </Visibility.VisibleFrom>
                    </StyledProductsHeader>
                    <MobileCategories menus={sidemenu} breadcrumbs={breadcrumbItems} />
                    <StyledProductList>
                        {gridLayout === 'with-menu' && (
                            <StyledCategoriesContainer>
                                <N40SideMenu menus={sidemenu} breadcrumbs={breadcrumbItems} />
                            </StyledCategoriesContainer>
                        )}
                        <StyledProductListContainer>
                            <DynamicFilters facets={['*']} maxValuesPerFacet={100} />
                            <ProductsGrid
                                cache={initialState ? null : algoliaCache}
                                hitsPerPage={hitsPerPage}
                                marketingContent={marketingContent}
                                isProductListPage
                            />

                            <NoResults type="plp" />
                        </StyledProductListContainer>
                    </StyledProductList>
                </AlgoliaClientSearch>
            )}
        </Container>
    );
});
